import React from 'react';
import DayJs from 'utils/Dayjs';
import Document from 'documents';
import { useRequest } from 'hooks';

import Show from 'components/Show';
import Avatar from 'components/Misc/Avatar';
import { Button } from 'components/Misc/Form';
import { Title } from 'components/Misc/Typography';

import Finals from 'containers/Administrator/Grades/Profile/Certificates/Finals';
import Awards from 'containers/Administrator/Grades/Profile/Certificates/Awards';
import Subjects from 'containers/Administrator/Grades/Profile/Certificates/Subjects';
import Settings from 'containers/Administrator/Grades/Profile/Certificates/Settings';

import { useGradeProfileStore } from 'store/grade/useGradeProfileStore';
import { useGradeCertificateStore } from 'store/grade/useGradeCertificateStore';

const Student = ({ student }) => {
	// Use grade's profile store
	const [grade, program] = useGradeProfileStore(state => [state.grade, state.program]);
	const onUpdateCertificate = useGradeCertificateStore(state => state.onUpdateCertificate);

	// Store request
	const [, request] = useRequest('put', `certificates/${student.certificate.id}`);

	// Handle certificate printing
	const onPrintCertificate = async () => {
		// Update certificates print timestamp
		const [success] = await request({
			issuedCertificate: new Date().toISOString()
		}, {
			type:'timestamp:certificate'
		});

		// Print certificate for middle school
		if (success && grade.number === 9) {
			onUpdateCertificate(success.certificate);
			Document('certificate-middle', {
				action: 'print'
			}, student);
		}

		// Print certificate for high school
		if (success && grade.number === 12) {
			onUpdateCertificate(success.certificate);
			Document('certificate-high', {
				action: 'print'
			}, student);
		}
	};

	// Handle appendix printing
	const onPrintAppendix = async () => {
		// Update certificates print timestamp
		const [success] = await request({
			issuedAppendix: new Date().toISOString()
		}, {
			type: 'timestamp:certificate'
		});

		// Print certificate for middle school
		if (success && grade.number === 9) {
			onUpdateCertificate(success.certificate);
			Document('appendix-middle', {
				action: 'print'
			}, student, program);
		}

		// Print certificate for high school
		if (success && grade.number === 12) {
			onUpdateCertificate(success.certificate);
			Document('appendix-high', {
				action: 'print'
			}, student, program);
		}
	};

	// Handle certificate download
	const onDownloadCertificate = () => {
		if (grade.number === 9) {
			Document('certificate-middle', {
				action: 'download'
			}, student);
		} else {
			Document('certificate-high', {
				action: 'download'
			}, student);
		}
	};

	// Handle appendix download
	const onDownloadAppendix = () => {
		if (grade.number === 9) {
			Document('appendix-middle', {
				action: 'download'
			}, student, program);
		} else {
			Document('appendix-high', {
				action: 'download'
			}, student, program);
		}
	};

	return (
		<div className="p-4 space-y-2">
			{/* Student's data */}
			<div className="flex items-center space-x-2">
				<div className="flex flex-1 items-center space-x-2">
					<Avatar
						small
						src={student.avatar}
						alt={student.name}
						online={student.online}
					/>

					<Title
						title={`${student.name} ${student.surname}`}
						className="flex-1"
					/>
				</div>

				<Button
					small
					square
					icon="link"
					to={`/users/${student.id}`}
				/>
			</div>

			<div className="py-2 grid grid-cols-2 gap-4">
				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Personas kods
					</div>

					<div className="">
						{student.personalCode}
					</div>

					<Show if={!student.personalCode}>
						<span className="text-rose-500">Nav ievadits!</span>
					</Show>
				</div>

				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Apguva
					</div>

					<div className="">
						{program.title}
					</div>

					<Show if={!program.title}>
						<span className="text-rose-500">Nav ievadits!</span>
					</Show>
				</div>
			</div>

			{/* Student's subjects */}
			<Subjects student={student} />

			{/* Student's finals */}
			<Finals studentId={student.id} finals={student.finals} />

			{/* Student's awards */}
			<Awards studentId={student.id} awards={student.awards} />

			{/* Certificate's settings */}
			<Settings
				studentId={student.id}
				certificate={student.certificate}
			/>

			{/* Print and download certificate */}
			<div className="flex items-end gap-2">
				<div className="flex-1 flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Atestāts izdrukāts:
					</div>

					<div className="">
						{student.certificate.issuedCertificate ? DayJs(student.certificate.issuedCertificate).format('LLL') : 'Nav'}
					</div>
				</div>

				<div className="flex gap-2">
					<Button
						square
						icon="download"
						onClick={onDownloadCertificate}
					/>

					<Button
						square
						icon="print"
						onClick={onPrintCertificate}
					/>
				</div>
			</div>

			{/* Print and download appendix */}
			<div className="flex items-end gap-2">
				<div className="flex-1 flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Sekmju izraksts izdrukāts:
					</div>

					<div className="">
						{student.certificate.issuedAppendix ? DayJs(student.certificate.issuedAppendix).format('LLL') : 'Nav'}
					</div>
				</div>

				<div className="flex gap-2">
					<Button
						square
						icon="download"
						onClick={onDownloadAppendix}
					/>

					<Button
						square
						icon="print"
						onClick={onPrintAppendix}
					/>
				</div>
			</div>
		</div>
	);
};

export default Student;